<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap space-x-4 mb-4">
      <vs-button
        class="btn-icon"
        color="primary"
        :type="showListCamera ? 'filled' : 'border'"
        icon="videocam"
        @click="showListCamera = !showListCamera"
      />
      <vs-select
        ref="view"
        autocomplete
        class="selectExample"
        v-model="selectedView"
      >
        <vs-select-item
          v-for="(item, index) in views"
          :key="'view-' + (item.id !== undefined ? item.id : index)"
          :value="item.id"
          :text="item.name"
        />
      </vs-select>
      <vs-dropdown role="button" vs-trigger-click>
        <vs-button
          class="btn-icon"
          color="primary"
          type="border"
          icon="view_module"
        />
        <vs-dropdown-menu>
          <vs-dropdown-item
            v-for="grid in grids"
            :key="grid.label"
            @click="changeGrid(grid)"
          >
            <span :class="{ 'font-bold': grid.id === selectedGrid }">
              {{ grid.label }}
            </span>
          </vs-dropdown-item>
        </vs-dropdown-menu>
      </vs-dropdown>

      <vs-dropdown role="button" vs-trigger-click>
        <vs-button
          class="btn-icon"
          color="primary"
          type="flat"
          icon="more_horiz"
        />
        <vs-dropdown-menu>
          <vs-dropdown-item @click="createNewView">
            Save View
          </vs-dropdown-item>
          <vs-dropdown-item v-if="!isNewView" @click="confirmDeleteView">
            Delete View
          </vs-dropdown-item>
        </vs-dropdown-menu>
      </vs-dropdown>
    </div>
    <!-- Content -->
    <div class="flex overflow-x-hidden">
      <aside class="sidebar" :class="{ 'sidebar--show': showListCamera }">
        <span
          v-if="isLoadingCamera"
          class="w-full py-2 text-center text-grey font-semibold animate-pulse"
        >
          Loading...
        </span>
        <template v-else>
          <!-- Info -->
          <div class="w-full flex items-center p-2">
            <span class="font-semibold mr-3"> Total Camera </span>
            <span
              class="h-6 px-2 inline-block rounded-full bg-primary text-white"
            >
              {{ totalCamera }}
            </span>
          </div>
          <!-- Filter -->
          <div class="w-full flex flex-col px-2 mb-3">
            <vs-input
              v-model="sidebar.search"
              icon="search"
              placeholder="Filter"
              class="search-input"
            />
          </div>
          <!-- Tree View -->
          <vue-custom-scrollbar class="treview-wrapper">
            <ul class="sidebar__list">
              <li
                v-for="(site, siteIndex) in filteredCamera"
                :key="'site-' + (site.siteId !== undefined ? site.siteId : siteIndex) + '-' + siteIndex"
                :class="{
                  'sidebar__list-item--open': site.show,
                }"
                class="sidebar__list-item"
              >
                  <div @click="site.show = !site.show">
                    <vs-icon icon="arrow_right" />
                    <vs-icon :icon="site.show ? 'folder_open' : 'folder'" />
                    <span>
                      {{ site.siteName }}
                    </span>
                  </div>
                  <ul v-show="site.show" class="sidebar__list">
                    <li
                      v-for="(area, areaIndex) in site.areaTree"
                      :key="'area-' + (area.areaId !== undefined ? area.areaId : areaIndex) + '-' + areaIndex"
                      :class="{
                        'sidebar__list-item--open': area.show,
                      }"
                      class="sidebar__list-item"
                    >
                  <div @click="area.show = !area.show; fetchAreaCameras(site, area, sidebar.search)">
                    <vs-icon icon="arrow_right" />
                    <vs-icon
                      v-if="area.isLoading"
                      icon="loop"
                      :animation="'spin'"
                    />
                    <vs-icon v-else :icon="area.show ? 'folder_open' : 'folder'" />
                    <span>
                      {{ area.areaName }}
                    </span>
                  </div>
                      <draggable
                        tag="ul"
                        :list="area.cameraTree"
                        :group="{ name: 'camera', pull: 'clone', put: false }"
                        :sort="false"
                        :clone="onClone"
                        v-show="area.show"
                        class="sidebar__list"
                        @end="onDragEnd"
                      >
                        <li
                          v-for="(camera, camIndex) in area.cameraTree"
                          :key="'s-cam-' + (camera.cameraId !== undefined ? camera.cameraId : camIndex) + '-' + camIndex"
                          class="sidebar__list-item"
                        >
                          <div>
                            <vs-icon icon="videocam" />
                            <span>
                              {{ camera.cameraName }}
                            </span>
                          </div>
                        </li>
                      </draggable>
                    </li>
                  </ul>
                </li>
            </ul>
          </vue-custom-scrollbar>
        </template>
      </aside>
      <div class="content-video" :class="{ 'ml-4': showListCamera }">
        <draggable
          class="drag-content"
          :class="gridColClass"
          :list="draggedData"
          :sort="false"
          group="camera"
          :animation="150"
          @add="onCameraPlaced"
        >
          <div
            v-for="(item, index) in filteredDraggedData"
            :key="'cam-' + item.cameraId"
          >
            <button
              title="Remove Camera"
              class="remove-button"
              @click.prevent="removeCamera(index)"
            >
              <vs-icon icon="close" color="white" />
            </button>
            <div v-if="!item.streamingURL" class="video-placeholder">
              <div class="loader">Loading stream...</div>
              <div class="mt-2">
                <vs-button
                  size="small"
                  color="warning"
                  type="flat"
                  @click.prevent="fetchStream(index)"
                >
                  Retry
                </vs-button>
              </div>
            </div>
            <Player
              v-if="item.show && item.streamingURL"
              :key="item.cameraId + '-' + (item._reload || item.timeStart)"
              :title="item.cameraName"
              :isPTZ="item.isPTZ"
              :url="item.streamingURL"
              :options="videoOptions"
              :resize="resizeVideo"
              :rewind="30"
              is-muted
              is-mic-muted
              :time-start="item.timeStart"
              @skip="onSkip($event, item, index)"
              @action:export="onExport(item)"
              @action:bookmark="onBookmark(item)"
              @action:snapshot="onSnapshot(item)"
              @action:record="onRecord($event, item)"
              @timeline:click="onSkip($event, item, index)"
              @quality:change="onQualityChange($event, item, index)"
              @action:ptzup="movePTZ(item, 'up')"
              @action:ptzleft="movePTZ(item, 'left')"
              @action:ptzright="movePTZ(item, 'right')"
              @action:ptzdown="movePTZ(item, 'down')"
            />
            <!-- Snapshot Animation -->
            <!--  -->
            <transition name="snapshot" mode="out-in">
              <div v-if="item.cameraId === isSnapshot" class="snapshot-screen">
                <span class="snapshot-icon">
                  <vs-icon
                    icon="filter_center_focus"
                    size="64px"
                    color="black"
                  />
                </span>
              </div>
            </transition>
          </div>
          <!-- Fill it when empty -->
          <div v-for="child in childsGrid" :key="'child-' + child" class="child"></div>
        </draggable>
        <!-- Shadow Grid -->
        <div class="shadow-grid" :class="gridColClass">
          <div v-for="column in columns" :key="'col-' + column"></div>
        </div>
      </div>
    </div>
    <!-- Modal Download -->
    <Modal
      :show="showDownloadModal"
      size="sm"
      centered
      :close-by-backdrop="false"
      @close="closeDownloadModal"
    >
      <div class="bg-white flex flex-col rounded">
        <div class="px-4 pt-4">
          <span class="text-lg font-bold uppercase">Download Video</span>
        </div>
        <div class="p-4">
          <p
            class="text-sm p-3 mb-2 bg-grey-light rounded"
            :class="{
              'text-red bg-red bg-opacity-25': downloadMax > 60,
            }"
          >
            Download maximum time 60 minutes.
          </p>
          <div class="text-sm font-semibold">
            Selected Camera:
            <span class="text-black">
              {{ selectedCamera.cameraName || "unknown" }}
            </span>
          </div>
          <div class="w-full h-px bg-grey my-4" />
          <p v-if="formError" class="text-red text-sm mb-2">
            {{ formError }}
          </p>
          <div class="flex flex-wrap -mx-4">
            <div class="w-full px-4 mb-3">
              <label for="date" class="block mb-2 font-medium"
                >Start Date</label
              >
              <flat-pickr
                class="vs-inputx vs-input--input normal"
                v-model="downloadForm.date"
                :config="dateConfig"
              />
            </div>
            <div class="w-1/2 px-4 mb-3">
              <label for="date" class="block mb-2 font-medium"
                >Start Time</label
              >
              <flat-pickr
                class="vs-inputx vs-input--input normal"
                v-model="downloadForm.startTime"
                :config="timeConfig"
                placeholder="Start time"
              />
            </div>
            <div class="w-1/2 px-4 mb-4">
              <label for="date" class="block mb-2 font-medium">End Time</label>
              <flat-pickr
                class="vs-inputx vs-input--input normal"
                v-model="downloadForm.endTime"
                :config="timeConfig"
                placeholder="End time"
              />
            </div>
            <div class="w-full px-4">
              <label for="date" class="block mb-2 font-medium">Quality</label>
              <div class="space-x-5">
                <vs-radio
                  v-model="downloadForm.quality"
                  vs-name="quality-low"
                  vs-value="1"
                >
                  Low
                </vs-radio>
                <vs-radio
                  v-model="downloadForm.quality"
                  vs-name="quality-high"
                  vs-value="2"
                >
                  High
                </vs-radio>
              </div>
            </div>
            <div class="w-full h-px bg-grey my-4" />
            <div class="w-1/4 px-4">
              <label for="date" class="block font-medium">Duration</label>
              <strong>{{ videoDownloadDuration }}</strong>
            </div>
            <div class="w-3/4 text-right px-4 space-x-2">
              <vs-button
                color="dark"
                type="flat"
                @click.native="closeDownloadModal"
              >
                Cancel
              </vs-button>
              <vs-button
                color="primary"
                type="filled"
                :disabled="formLoading || downloadMax > 60"
                @click.prevent="downloadAction"
              >
                {{ formLoading ? "Downloading..." : "Submit" }}
              </vs-button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    <!-- Modal Bookmark -->
    <Modal
      :show="showBookmarkModal"
      :order="2"
      size="sm"
      centered
      :close-by-backdrop="false"
      @close="closeBookmarkModal"
    >
      <div class="bg-white flex flex-col rounded">
        <div class="px-4 pt-4">
          <span class="text-lg font-bold uppercase">Bookmark Video </span>
        </div>
        <div class="p-4">
          <div class="text-sm font-semibold">
            Selected Camera:
            <span class="text-black">
              {{ selectedCamera.cameraName || "unknown" }}
            </span>
          </div>
          <div class="w-full h-px bg-grey my-4" />
          <p v-if="formError" class="text-red text-sm mb-2">
            {{ formError }}
          </p>
          <div class="flex flex-wrap -mx-4">
            <div class="w-full px-4 mb-3">
              <label for="date" class="block mb-2 font-medium">Name</label>
              <vs-input
                class="w-full"
                v-model="bookmarkForm.name"
                placeholder="Name"
              />
            </div>
            <div class="w-full px-4">
              <label for="date" class="block mb-2 font-medium"
                >Description</label
              >
              <vs-textarea
                v-model="bookmarkForm.description"
                placeholder="Description"
              />
            </div>
            <div class="w-full px-4 mb-3">
              <label for="date" class="block mb-2 font-medium"
                >Start Date</label
              >
              <flat-pickr
                class="vs-inputx vs-input--input normal"
                v-model="bookmarkForm.date"
                :config="dateConfig"
              />
            </div>
            <div class="w-1/2 px-4 mb-3">
              <label for="date" class="block mb-2 font-medium"
                >Start Time</label
              >
              <flat-pickr
                v-if="showBookmarkModal"
                class="vs-inputx vs-input--input normal"
                v-model="bookmarkForm.startTime"
                :config="timeConfigStart"
                placeholder="Start time"
              />
            </div>
            <div class="w-1/2 px-4 mb-4">
              <label for="date" class="block mb-2 font-medium">End Time</label>
              <flat-pickr
                v-if="showBookmarkModal"
                class="vs-inputx vs-input--input normal"
                v-model="bookmarkForm.endTime"
                :config="timeConfigEnd"
                placeholder="End time"
              />
            </div>
            <div class="w-full h-px bg-grey my-4" />
            <div class="w-1/4 px-4">
              <label for="date" class="block font-medium">Duration</label>
              <strong>{{ bookmarkDuration }}</strong>
            </div>
            <div class="w-3/4 text-right px-4 space-x-2">
              <vs-button
                color="dark"
                type="flat"
                @click.native="closeBookmarkModal"
              >
                Cancel
              </vs-button>
              <vs-button
                color="primary"
                type="filled"
                :disabled="formLoading"
                @click.prevent="bookmarkAction"
              >
                {{ formLoading ? "Submitting..." : "Submit" }}
              </vs-button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    <!-- Modal Record -->
    <Modal
      :show="showRecordModal"
      :order="2"
      size="xs"
      centered
      :close-by-backdrop="false"
    >
      <div class="bg-white flex flex-col rounded">
        <div class="p-4 text-center">
          <span class="font-bold uppercase">Send Audio Recording</span>
        </div>
        <div class="px-4 py-6 text-center">
          <button
            class="outline-none border-none bg-transparent cursor-pointer"
            @click.prevent="!recorder ? startRecording() : stopRecording()"
          >
            <feather-icon
              :icon="recorder ? 'StopCircleIcon' : 'MicIcon'"
              svgClasses="text-blue"
            />
          </button>

          <div v-if="record.audio" class="w-full text-center mb-4">
            <vs-button
              color="dark"
              type="flat"
              size="small"
              @click.native="resetRecording"
            >
              Reset
            </vs-button>
          </div>

          <div v-if="record.audio" class="flex justify-center">
            <audio :src="newAudioURL" controls></audio>
          </div>
        </div>
        <div class="w-full text-center p-4 space-x-2">
          <vs-button
            color="dark"
            type="flat"
            @click.native="cancelRecording"
            :disabled="record.loading"
          >
            Cancel
          </vs-button>
          <vs-button
            color="primary"
            type="filled"
            :disabled="record.loading || !record.audio"
            @click.native="sendRecording"
          >
            {{ record.loading ? "Sending..." : "Send" }}
          </vs-button>
        </div>
      </div>
    </Modal>
    <!-- Download Bar -->
    <DownloadBar
      :is-downloading="isDownloading"
      :progress="percentageDownload"
      @close="isDownloading = false"
    />
  </div>
</template>
<script>
import {
  toDuration,
  time2seconds,
  removeDuplicates,
  removeSpaces,
  hasAttribute,
  GMTToLocalStringTime,
} from "@/composables";
import { grids, videoOptions, dateConfig, timeConfig } from "./states";
import Mixins from "@/composables/mixins";
import api from "@/api";

import vueCustomScrollbar from "vue-custom-scrollbar";
import flatPickr from "vue-flatpickr-component";
import draggable from "vuedraggable";
import Player from "@/components/player/VideoPlayer.vue";
import DownloadBar from "@/components/player/DownloadBar.vue";
import Modal from "@/components/modal/Modal.vue";
import "vue-custom-scrollbar/dist/vueScrollbar.css";
import "flatpickr/dist/flatpickr.css";
export default {
  name: "ViewsPage",
  components: {
    draggable,
    Modal,
    Player,
    flatPickr,
    vueCustomScrollbar,
    DownloadBar,
  },
  mixins: [Mixins],
  data: () => ({
    views: [],
    cameras: [],
    draggedData: [],
    // WebSocket connection state
    ws: null,
    wsUrl: process.env.VUE_APP_WS_URL || "https://192.168.200.248:7246",
    wsRetryCount: 0,
    wsMaxRetries: 10,
    wsReconnectBaseMs: 100,
    selectedView: 0,
    selectedCamera: {},
    lastCameraAdded: {},
    isLoadingCamera: false,
    showListCamera: false,
    showDownloadModal: false,
    showBookmarkModal: false,
    showRecordModal: false,
    isSnapshot: null,
    selectedGrid: 3, // grid 2x2
    formLoading: false,
    formError: null,
    form: {
      view: null,
    },
    downloadForm: {
      date: new Date(),
      quality: 1, // default quality is low quality
      startTime: null,
      endTime: null,
    },
    bookmarkForm: {
      name: null,
      description: null,
      date: new Date(),
      startTime: null,
      endTime: null,
    },
    recorder: null,
    record: {
      audio: null,
      chunks: [],
      loading: false,
    },
    resizeVideo: false,
    videoTimeStart: null,
    sidebar: {
      search: "",
    },
    grids,
    videoOptions,
    dateConfig,
    timeConfig,
    timeConfigStart: {
      ...timeConfig,
      defaultDate: null,
    },
    timeConfigEnd: {
      ...timeConfig,
      defaultDate: null,
    },
    // NEW: WebSocket improvements
    cameraRefreshIntervals: {}, // Store intervals per camera
    wsConnected: false,
  }),
  computed: {
    videoDownloadDuration() {
      if (this.downloadForm.startTime && this.downloadForm.endTime) {
        return toDuration({
          startTime: this.downloadForm.startTime,
          endTime: this.downloadForm.endTime,
        });
      }
      return "00:00:00";
    },
    downloadMax() {
      let max = 0;
      if (this.downloadForm.startTime && this.downloadForm.endTime) {
        max = Math.ceil(
          time2seconds(this.downloadForm.endTime) -
            time2seconds(this.downloadForm.startTime)
        );
        max = max / 60;
      }
      return max;
    },
    bookmarkDuration() {
      if (this.bookmarkForm.startTime && this.bookmarkForm.endTime) {
        return toDuration({
          startTime: this.bookmarkForm.startTime,
          endTime: this.bookmarkForm.endTime,
        });
      }
      return "00:00:00";
    },
    isNewView() {
      return this.selectedView === 0;
    },
    selectedLayout() {
      return this.grids.find((grid) => grid.id === this.selectedGrid);
    },
    columns() {
      const layout = this.selectedLayout;
      return Math.ceil(layout.x * layout.y);
    },
    gridColClass() {
      if (this.columns % 3 === 0) {
        return "grid-cols-3";
      } else if (this.columns % 2 === 0) {
        return "grid-cols-2";
      } else {
        return "grid-cols-1";
      }
    },
    childsGrid() {
      const calculateGrid =
        this.filteredDraggedData.length > 0 &&
        this.columns > this.filteredDraggedData.length;

      if (calculateGrid) {
        return Math.ceil(this.columns - this.filteredDraggedData.length);
      }
      return 0;
    },
    filteredDraggedData() {
      const remove = removeDuplicates(this.draggedData, "cameraId");
      return remove.filter((camera) => camera.show === true);
    },
    totalCamera() {
      // Sum totalCameras from all sites
      return this.cameras.reduce((total, site) => {
        return total + (site.totalCameras || 0);
      }, 0);
    },
    filteredCamera() {
      if (!this.sidebar.search) return this.cameras;
      return this.cameras.map((site, h, arr) => {
        return {
          ...site,
          show: arr[h].areaTree.length > 0,
          areaTree: site.areaTree.map((area, i, array) => {
            return {
              ...area,
              show: array[i].cameraTree.length > 0,
              cameraTree: area.cameraTree.filter((camera) =>
                camera.cameraName
                  .toLowerCase()
                  .includes(this.sidebar.search.toLowerCase())
              ),
            };
          }),
        };
      });
    },
    newAudioURL() {
      return this.record.audio ? URL.createObjectURL(this.record.audio) : null;
    },
  },
  watch: {
    showListCamera(val) {
      this.resizeVideo = !this.resizeVideo;
      if (val && this.cameras.length === 0) {
        this.getCameras();
      }
    },
    selectedGrid() {
      this.resizeVideo = !this.resizeVideo;
    },
    async selectedView(viewId) {
      window.localStorage.setItem("selectedView", viewId);
      if (viewId) {
        await this.getDetailView(viewId);
      } else {
        // if viewId is empty or 0
        this.resetViewData();
      }
    },
    showBookmarkModal: {
      handler() {
        this.initBookmarkTime();
      },
      immediate: true,
    },
    "sidebar.search"(newSearch) {
      // Refetch cameras for all open areas when search term changes
      this.cameras.forEach((site) => {
        site.areaTree.forEach((area) => {
          if (area.show) {
            // Reset loaded flag to force refetch with new search term
            area.camerasLoaded = false;
            this.fetchAreaCameras(site, area, newSearch);
          }
        });
      });
    },
  },
  async mounted() {
    await this.getAllData();
    this.getValueFromSelectInput();
    // init websocket to receive streaming URL updates
    // NOTE: websocket is opened when first camera is placed via onCameraPlaced()
  },
  beforeDestroy() {
    const selectedView = window.localStorage.getItem("selectedView");
    if (selectedView) {
      window.localStorage.removeItem("selectedView");
    }
    // close websocket on destroy
    this.closeWebSocket();
  },
  methods: {
    // Helper method to format current datetime in ISO 8601 format with timezone
    // This matches the format used in new.vue for proper Player component compatibility
    getFormattedDateTime() {
      const dateTime = new Date();
      const offsetMinutes = dateTime.getTimezoneOffset();
      const localTimeWithOffset = new Date(
        dateTime.getTime() - offsetMinutes * 60000
      );
      const formattedDateTime = localTimeWithOffset
        .toISOString()
        .replace("Z", "");
      
      const offsetSign = offsetMinutes >= 0 ? "-" : "+";
      const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
      const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60;
      const offsetString = `${offsetSign}${
        offsetHours < 10 ? "0" : ""
      }${offsetHours}:${
        offsetMinutesRemainder < 10 ? "0" : ""
      }${offsetMinutesRemainder}`;
      
      return formattedDateTime + offsetString;
    },

    // Initialize WebSocket connection
    // initWebSocket(userId) {
    //   if (!this.wsUrl || !userId) return;
    //   if (this.ws) return; // prevent multiple connections

    //   // Convert http/https to ws/wss
    //   let base = this.wsUrl;
    //   if (base.startsWith("http://")) base = "ws://" + base.slice(7);
    //   else if (base.startsWith("https://")) base = "wss://" + base.slice(8);
    //   else if (!base.startsWith("ws://") && !base.startsWith("wss://"))
    //     base = "ws://" + base;

    //   const wsUrl = `${base.replace(/\/$/, "")}/ws?userId=${encodeURIComponent(userId)}`;

    //   try {
    //     this.ws = new WebSocket(wsUrl);

    //     this.ws.onopen = () => {
    //       console.info("WebSocket connected:", wsUrl);
    //       this.wsConnected = true;
    //       // NEW: Start refresh intervals for all existing cameras
    //       this.startAllCameraRefreshes();
    //     };

    //     this.ws.onmessage = (evt) => {
    //       try {
    //         const data = JSON.parse(evt.data);
    //         if (data.type === "StreamingUrlUpdate") {
    //           // NEW: Use dedicated handler method
    //           this.handleStreamUpdate(data);
    //         }
    //       } catch (e) {
    //         console.error("Failed to parse WS message:", e);
    //       }
    //     };

    //     this.ws.onclose = () => {
    //       console.warn("WebSocket disconnected");
    //       this.wsConnected = false;
    //       this.ws = null;
    //       // NEW: Stop all refresh intervals when WS disconnects
    //       this.stopAllCameraRefreshes();
          
    //       // Simple retry logic
    //       if (this.wsRetryCount < this.wsMaxRetries) {
    //         this.wsRetryCount++;
    //         setTimeout(() => this.initWebSocket(userId), this.wsReconnectBaseMs * Math.pow(2, this.wsRetryCount));
    //       }
    //     };

    //     this.ws.onerror = (err) => {
    //       console.error("WebSocket error:", err);
    //       this.wsConnected = false;
    //       try {
    //         this.ws.close();
    //       } catch (e) {}
    //     };
    //   } catch (e) {
    //     console.error("Failed to create WebSocket:", e);
    //   }
    // },

    // NEW: Method to handle stream updates
    handleStreamUpdate(data) {
      const cameraId = data.cameraId || data.camId;
      if (!cameraId) {
        console.warn("Stream update received without camera ID:", data);
        return;
      }

      // Update in draggedData (main view)
      const idx = this.draggedData.findIndex(c => 
        String(c.cameraId) === String(cameraId) || 
        String(c.camId) === String(cameraId)
      );
      
      if (idx !== -1) {
        const streamUrl = data.StreamingURL || data.streamingURL;
        if (streamUrl) {
          const cleanUrl = streamUrl.replace(/\\u0026/g, "&");
          this.$set(this.draggedData[idx], "streamingURL", cleanUrl);
          this.$set(this.draggedData[idx], "timeStart", this.getFormattedDateTime());
          console.info("Updated streaming URL for camera:", cameraId, cleanUrl);
        }
      }

      // Also update in cameras tree (sidebar) if needed
      this.updateCameraInTree(cameraId, data.StreamingURL || data.streamingURL);
    },

    // NEW: Update camera in sidebar tree
    updateCameraInTree(cameraId, streamingURL) {
      if (!streamingURL) return;
      
      const cleanUrl = streamingURL.replace(/\\u0026/g, "&");
      
      this.cameras.forEach(site => {
        site.areaTree.forEach(area => {
          area.cameraTree.forEach(camera => {
            if (String(camera.cameraId) === String(cameraId) || 
                String(camera.camId) === String(cameraId)) {
              camera.streamingURL = cleanUrl;
              camera.timeStart = this.getFormattedDateTime();
            }
          });
        });
      });
    },

    // NEW: Start refresh interval for a specific camera
    startCameraRefresh(cameraId) {
      // Clear existing interval for this camera
      this.stopCameraRefresh(cameraId);
      
      // Start new interval - refresh every 5 minutes (300,000 ms)
      this.cameraRefreshIntervals[cameraId] = setInterval(() => {
        this.refreshCameraStream(cameraId);
      }, 300000); // 5 minutes
      
      console.log(`Started 5-min refresh for camera ${cameraId}`);
    },

    // NEW: Stop refresh interval for a specific camera
    stopCameraRefresh(cameraId) {
      if (this.cameraRefreshIntervals[cameraId]) {
        clearInterval(this.cameraRefreshIntervals[cameraId]);
        delete this.cameraRefreshIntervals[cameraId];
      }
    },

    // NEW: Refresh stream for a specific camera
    async refreshCameraStream(cameraId) {
      // if (!this.wsConnected) return; // Allow independent refresh

      try {
        console.log(`Refreshing stream for camera ${cameraId}`);
        // This will trigger the backend to send a new StreamingUrlUpdate via WebSocket
        const { data } = await api.viewCameraStream({ cameraId: cameraId });
        const streamingURL =
          Array.isArray(data.data) && data.data[0]
            ? data.data[0].streamingURL
            : null;

        if (streamingURL) {
          const cleanUrl = streamingURL.replace(/\\u0026/g, "&");
          
          // iterate all draggedData to find the camera
           this.draggedData.forEach((camera, index) => {
             if (String(camera.cameraId) === String(cameraId)) {
                console.info(
                  "Refreshed streaming URL for camera:",
                  cameraId,
                  cleanUrl
                );
                // Update properties
                this.$set(this.draggedData[index], "streamingURL", cleanUrl);
                this.$set(this.draggedData[index], "_reload", Date.now());
                this.$set(this.draggedData[index], "timeStart", this.getFormattedDateTime());
             }
           });
        }
      } catch (error) {
        console.error(
          `Failed to refresh stream for camera ${cameraId}:`,
          error
        );
      }
    },

    // NEW: Start refreshes for all cameras in view
    startAllCameraRefreshes() {
      this.draggedData.forEach((camera) => {
        const cameraId = camera.cameraId || camera.camId;
        if (cameraId) {
          this.startCameraRefresh(cameraId);
        }
      });
    },

    // NEW: Stop all refresh intervals
    stopAllCameraRefreshes() {
      Object.keys(this.cameraRefreshIntervals).forEach((cameraId) => {
        clearInterval(this.cameraRefreshIntervals[cameraId]);
      });
      this.cameraRefreshIntervals = {};
    },

    // Close and cleanup WebSocket connection
    closeWebSocket() {
      // NEW: Stop all refresh intervals first
      this.stopAllCameraRefreshes();
      
      if (!this.ws) return;
      try {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.close();
      } catch (e) {
        console.error("Error closing websocket:", e);
      } finally {
        this.ws = null;
        this.wsConnected = false;
      }
    },

    async getAllData() {
      this.$vs.loading();
      // load view
      const selectedView = window.localStorage.getItem("selectedView");
      let callbackView;

      if (parseInt(selectedView)) {
        this.selectedView = selectedView;
        callbackView = await this.getDetailView(selectedView);
      }

      Promise.all([this.getViews(), callbackView])
        .then(() => {
          this.$vs.loading.close();
        })
        .catch(() => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Failed",
            text: "Error while getting all data",
            color: "danger",
            position: "top-right",
          });
        });
    },
    async getViews() {
      const { data } = await api.viewList();
      this.views = [{ id: 0, name: "Create New View" }, ...data.data];
    },
    async fetchAreaCameras(site, area, cameraName = null) {
      // Only fetch if not already loaded (unless searching)
      if (area.camerasLoaded && !cameraName) return;

      try {
        area.isLoading = true;
        const params = {
          siteId: site.siteId,
          areaId: area.areaId,
        };
        
        // Add cameraName parameter if search is provided
        if (cameraName && cameraName.trim()) {
          params.cameraName = cameraName.trim();
        }

        const { data: cameraData } = await api.getCameraTreeByArea(params);

        // Extract camera tree from response
        let cameraTree = [];
        if (Array.isArray(cameraData.data) && cameraData.data.length > 0) {
          // Handle nested array structure
          const firstElement = cameraData.data[0];
          if (Array.isArray(firstElement)) {
            // If first element is array, it's the nested structure
            if (firstElement.length > 0 && firstElement[0].areaTree) {
              cameraTree = firstElement[0].areaTree[0].cameraTree || [];
            }
          } else if (firstElement.areaTree) {
            // If first element has areaTree, extract cameras
            cameraTree = firstElement.areaTree[0].cameraTree || [];
          } else if (firstElement.cameraId) {
            // If first element is a camera object, use the array directly
            cameraTree = cameraData.data;
          }
        }

        // Update the area with cameras
        area.cameraTree = cameraTree.map((camera) => {
          return {
            ...camera,
            timeStart: this.getFormattedDateTime(),
            show: true,
          };
        });
        area.camerasLoaded = true;

        console.log(`Loaded ${cameraTree.length} cameras for area ${area.areaId}`);
      } catch (err) {
        console.error(`Failed to fetch cameras for area ${area.areaId}:`, err);
        this.$vs.notify({
          title: "Failed",
          text: `Error loading cameras for ${area.areaName}`,
          color: "danger",
          position: "top-right",
        });
      } finally {
        area.isLoading = false;
      }
    },
    async getCameras() {
      try {
        this.isLoadingCamera = true;
        // Step 1: Get sites and areas only (don't fetch cameras yet)
        const { data: sitesData } = await api.getSiteAreaTree();
        let sites = sitesData && sitesData.data ? sitesData.data : [];

        const treeView = sites.map((site, siteIndex) => {
          return {
            siteId: site.siteId,
            siteName: site.siteName,
            totalCameras: site.totalCameras || 0, // Preserve totalCameras from API
            show: false, // closed by default
            areaTree: (site.areaTree || []).map((area, areaIndex) => {
              return {
                areaId: area.areaId,
                areaName: area.areaName,
                show: false, // closed by default
                cameraTree: [], // Empty initially, will be fetched on demand
                camerasLoaded: false, // Track if cameras have been loaded
                isLoading: false, // Track loading state
              };
            }),
          };
        });

        this.cameras = treeView;
      } catch (error) {
        this.$vs.notify({
          title: "Failed",
          text: "Error while fetching cameras",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.isLoadingCamera = false;
      }
    },
    initBookmarkTime() {
      const now = new Date();
      const start = new Date(now.getTime() - 60 * 1000);

      // set default time
      this.timeConfigStart.defaultDate = start;
      this.timeConfigEnd.defaultDate = now;
      // set default value

      this.bookmarkForm.startTime = GMTToLocalStringTime(start).substr(11, 8);
      this.bookmarkForm.endTime = GMTToLocalStringTime(now).substr(11, 8);
    },
    getValueFromSelectInput() {
      const selectref = this.$refs.view.$el;
      const inputEl = selectref.querySelector("input");
      const self = this;
      inputEl.addEventListener("change", function (e) {
        const value = e.target.value;
        const findIndex = self.views.findIndex(
          (view) => view.id === self.selectedView
        );
        self.views[findIndex].name = value;
        self.form.view = value;
      });
    },
    async getDetailView(viewId) {
      try {
        this.$vs.loading();
        if (viewId) {
          const { data } = await api.viewDetail({
            webCameraGroupId: viewId,
          });
          const detail = data.data[0];

          const cameras = detail.webCamera.map((camera) => {
            return {
              ...camera,
              show: true,
              timeStart: this.getFormattedDateTime(),
            };
          });
          const selectedGrid = this.grids.find(
            (grid) => removeSpaces(grid.label) === removeSpaces(detail.size)
          );
          this.form.view = detail.name; // this for editing purposes
          this.selectedGrid = selectedGrid.id;
          this.draggedData = cameras;
          // Start refresh intervals for loaded cameras
          this.startAllCameraRefreshes();
          this.$vs.loading.close();
        }
      } catch (error) {
        this.$vs.loading.close();
        this.$vs.notify({
          title: "Failed",
          text: "Error while getting detail view",
          color: "danger",
          position: "top-right",
        });
      }
    },
    resetViewData() {
      this.stopAllCameraRefreshes();
      this.form.view = null;
      this.draggedData = [];
      this.selectedView = 0;
      this.selectedGrid = 3;
    },
    async createNewView() {
      try {
        const isEditing = !this.isNewView;

        const webCamera = this.filteredDraggedData.map(({ cameraId }) => {
          return { cameraId };
        });
        const size = this.selectedLayout.label;

        if (webCamera.length === 0) {
          this.$vs.dialog({
            color: "warning",
            title: `Warning`,
            text: "Please add camera first",
            acceptText: "Close",
          });
        } else if (
          this.form.view === "Create New View" ||
          this.form.view === null
        ) {
          this.$vs.dialog({
            color: "warning",
            title: `Warning`,
            text: "The view name is not allowed",
            acceptText: "Close",
          });
        } else {
          const params = {
            name: this.form.view,
            size: removeSpaces(size), // remove spacing
            webCamera: webCamera,
          };
          // if context is editing
          if (isEditing) {
            params.id = this.selectedView;
          }
          // save
          await api.viewSave(params);
          // load new views
          await this.getViews();
          // get selected view
          const selectedView = this.views.find(
            (view) => view.name === this.form.view
          );
          this.selectedView = selectedView.id;
          // notify
          this.$vs.notify({
            title: "Success",
            text: "View successfully saved",
            color: "success",
            position: "top-right",
          });
        }
      } catch (error) {
        this.$vs.notify({
          title: "Failed",
          text: "View failed to save",
          color: "danger",
          position: "top-right",
        });
      }
    },
    confirmDeleteView() {
      this.$vs.dialog({
        type: "confirm",
        color: "warning",
        title: `Warning`,
        text: "Are you sure you want to delete this view?",
        cancelText: "No",
        acceptText: "Yes",
        accept: this.deleteView,
      });
    },
    async deleteView() {
      try {
        const webCamera = this.filteredDraggedData.map(({ cameraId }) => {
          return { cameraId };
        });
        const size = this.selectedLayout.label;

        const params = {
          id: this.selectedView,
          name: this.form.view,
          size: removeSpaces(size), // remove spacing
          webCamera: webCamera,
        };
        // delete the selected
        const {
          data: { message },
        } = await api.viewDelete(params);
        // load new views
        await this.getViews();
        // reset view
        this.resetViewData();
        // notify
        this.$vs.notify({
          title: "Deleted",
          text: message,
          color: "success",
          position: "top-right",
        });
      } catch (error) {
        this.$vs.notify({
          title: "Failed",
          text: error.response.data.message,
          color: "danger",
          position: "top-right",
        });
      }
    },
    onExport(camera) {
      this.showDownloadModal = true;
      this.selectedCamera = camera;
    },
    closeDownloadModal() {
      this.showDownloadModal = false;
      this.formError = null;
      this.selectedCamera = {};
      this.timeConfigStart.defaultDate = null;
      this.timeConfigEnd.defaultDate = null;
      // reset form
      for (const key in this.downloadForm) {
        this.downloadForm[key] = null;
      }
    },
    async downloadAction() {
      try {
        this.formLoading = true;
        const areValid = Object.values(this.downloadForm).every((val) => val);
        if (!areValid) {
          this.formLoading = false;
          this.formError = "Semua field wajib diisi";
          return;
        }
        if (this.downloadMax > 60) {
          this.formLoading = false;
          this.formError = "Maksimal download 60 menit";
          return;
        }
        // are valid input
        const startTime = GMTToLocalStringTime(
          `${this.downloadForm.date} ${this.downloadForm.startTime}`
        );
        const endTime = GMTToLocalStringTime(
          `${this.downloadForm.date} ${this.downloadForm.endTime}`
        );

        const params = {
          cameraId: this.selectedCamera.cameraId,
          StartTime: startTime,
          EndTime: endTime,
          quality: parseInt(this.downloadForm.quality),
        };

        const { data } = await api.viewDownload(params);

        const streamingURL = data.data[0].streamingURL;
        const fileName = `${this.selectedCamera.cameraName}-${this.downloadForm.date}-${this.downloadForm.startTime}-${this.downloadForm.endTime}`;

        await this.downloadFile(streamingURL, "video/mp4", fileName);

        this.formLoading = false;
        this.closeDownloadModal();

        // await downloadFile(streamingURL, fileName, "mp4");
      } catch (error) {
        if (hasAttribute(error.response, "data")) {
          this.formError = error.response.data.message;
        }
        this.formLoading = true;
      }
    },
    removeCamera(index) {
      const confirmed = confirm("Are you sure you want to remove this camera?");
      if (confirmed) {
        // NEW: Stop refresh interval for this camera
        const camera = this.draggedData[index];
        if (camera && camera.cameraId) {
          this.stopCameraRefresh(camera.cameraId);
        }
        
        this.draggedData.splice(index, 1);
        if (this.draggedData.length === 0) {
          this.closeWebSocket();
        }
      }
    },
    makeHideAllCamera() {
      let data = [];
      for (let i = 0; i < this.draggedData.length; i++) {
        const item = this.draggedData[i];
        item.show = false;
        data.push(item);
      }
      return data;
    },
    makeShowSomeCamera(length) {
      let data = [];
      for (let i = 0; i < length; i++) {
        const camera = this.draggedData[i];
        if (camera) {
          camera.show = true;
          data.push(camera);
        }
      }
      return data;
    },
    changeGrid(grid) {
      this.selectedGrid = grid.id;
      if (this.draggedData.length) {
        // grid layout
        const gridCol = Math.ceil(grid.y * grid.x);
        // reset show to false
        const hideCameras = this.makeHideAllCamera();
        // set all camera show to false
        this.draggedData = hideCameras;
        // update show to true by column length
        const showCameraByGrid = this.makeShowSomeCamera(gridCol);
        // merge all camera show: false or show: true
        const arrMerged = [...this.draggedData, ...showCameraByGrid];
        // fix duplicates
        this.draggedData = removeDuplicates(arrMerged, "cameraId");
      }
    },
    onClone(camera) {
      const founded = this.draggedData.find(
        (cam) => cam.cameraId === camera.cameraId
      );
      if (!founded) {
        this.lastCameraAdded = camera;
        return {
          ...camera,
        };
      }
      this.$vs.dialog({
        color: "warning",
        title: `Warning`,
        text: "Camera is already added",
        acceptText: "Close",
      });
    },
    onDragEnd() {
      // prevent add camera when layout not available
      if (this.columns < this.draggedData.length) {
        this.$vs.dialog({
          color: "warning",
          title: `Warning`,
          text: "Layout size not available!",
          acceptText: "Close",
        });
        // remove last camera drag
        const index = this.draggedData.findIndex(
          (camera) => camera.cameraId === this.lastCameraAdded.cameraId
        );
        this.draggedData.splice(index, 1);
      }
    },
    // Bookmark
    onBookmark(camera) {
      this.showBookmarkModal = true;
      this.selectedCamera = camera;
    },
    closeBookmarkModal() {
      this.showBookmarkModal = false;
      this.formError = null;
      this.selectedCamera = {};
      for (const key in this.bookmarkForm) {
        this.bookmarkForm[key] = null;
      }
    },
    async bookmarkAction() {
      try {
        this.formLoading = true;

        const areValid = Object.values(this.bookmarkForm).every((val) => val);
        if (!areValid) {
          this.formLoading = false;
          this.formError = "Semua field wajib diisi";
          return;
        }

        const startTime = GMTToLocalStringTime(
          `${this.bookmarkForm.date} ${this.bookmarkForm.startTime}`
        );
        const endTime = GMTToLocalStringTime(
          `${this.bookmarkForm.date} ${this.bookmarkForm.endTime}`
        );
        // params
        const params = {
          cameraId: this.selectedCamera.cameraId,
          name: this.bookmarkForm.name,
          description: this.bookmarkForm.description,
          startTime: startTime,
          endTime: endTime,
        };
        // // save
        await api.viewBookmark(params);
        // set loading to false
        this.formLoading = false;
        // close modal
        this.closeBookmarkModal();
        // notify
        this.$vs.notify({
          title: "Success",
          text: "Bookmark successfully saved",
          color: "success",
          position: "top-right",
        });
      } catch (error) {
        if (hasAttribute(error.response, "data")) {
          this.formError = error.response.data.message;
        }
        this.formLoading = false;
      }
    },
    // take the snapshot
    async onSnapshot(camera) {
      try {
        this.isSnapshot = camera.cameraId;
        const { data } = await api.viewSnapshot({ cameraId: camera.cameraId });
        const streamingURL = data.data[0].streamingURL;
        const cameraName = `${camera.cameraName}-${new Date().toLocaleString(
          "ID"
        )}`;

        setTimeout(() => {
          this.isSnapshot = null;
        }, 250);

        await this.downloadFile(streamingURL, "image/jpeg", cameraName);
      } catch (error) {
        throw new Error(error.response.data.message);
      }
    },
    // action PTZ Control
    async movePTZ(camera, direction) {
      this.selectedCamera = camera;
      var left = 0;
      var right = 0;
      var up = 0;
      var down = 0;
      //call api PTZ
      if (direction == "left") {
        left = 1;
      }
      if (direction == "right") {
        right = 1;
      }

      if (direction == "up") {
        up = 1;
      }

      if (direction == "down") {
        down = 1;
      }

      var test = {
        cameraId: this.selectedCamera.cameraId,
        left: left,
        right: right,
        up: up,
        down: down,
        start: 1,
        stop: 0,
      };

      console.log("test " + JSON.stringify(test));

      await api
        .movePTZ({
          cameraId: this.selectedCamera.cameraId,
          left: left,
          right: right,
          up: up,
          down: down,
          start: 1,
          stop: 0,
        })
        .then((result) => result.data)
        .then((data) => {
          if (data.status) {
            this.lists = data.data;
          }
        })
        .catch((e) => {
          console.log(e);
        });
    },
    async onSkip(duration, item, index) {
      try {
        const startTime = GMTToLocalStringTime(duration);
        const { data } = await api.viewSkipVideo({
          cameraId: item.cameraId,
          startTime: startTime,
        });

        this.$set(
          this.draggedData[index],
          "streamingURL",
          data.data[0].streamingURL
        );
        this.$set(this.draggedData[index], "timeStart", duration);
      } catch (error) {
        throw new Error(error);
      }
    },
    async onQualityChange(quality, item, index) {
      try {
        const { data } = await api.viewSkipVideo({
          cameraId: item.cameraId,
          quality: quality,
        });
        this.$set(
          this.draggedData[index],
          "streamingURL",
          data.data[0].streamingURL
        );
      } catch (error) {
        throw new Error(error);
      }
    },
    onRecord(allow, camera) {
      this.showRecordModal = true;
      this.selectedCamera = camera;
    },
    async startRecording() {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      const SELF = this;
      const options = { mimeType: "audio/webm" };
      this.recorder = new MediaRecorder(stream, options);
      this.recorder.start();

      this.recorder.addEventListener("dataavailable", (e) => {
        if (e.data.size > 0) {
          SELF.record.chunks.push(e.data);
        }
      });
    },
    stopRecording() {
      const SELF = this;
      this.recorder.stop();
      this.recorder.addEventListener("stop", () => {
        SELF.record.audio = new Blob(SELF.record.chunks);
      });
      this.recorder = null;
    },
    resetRecording() {
      this.recorder = null;
      this.record.chunks = [];
      this.record.audio = null;
    },
    cancelRecording() {
      this.resetRecording();
      this.showRecordModal = false;
    },
    async sendRecording() {
      this.record.loading = true;

      const form = new FormData();
      form.append("CamId", this.selectedCamera.cameraId);
      form.append(
        "file",
        this.record.audio,
        "voice_" + new Date().getMilliseconds
      );

      api
        .uploadCamVoice(form)
        .then((result) => result.data)
        .then(() => {
          this.record.loading = false;
          this.cancelRecording();
        })
        .catch(() => {
          this.record.loading = false;
          this.cancelRecording();
        });
    },
    async fetchStream(index) {
      const camera = this.draggedData[index];
      if (!camera || !camera.cameraId) return;
      this.$set(this.draggedData[index], "loadingStream", true);
      try {
        const res = await api.viewCameraStream({ cameraId: camera.cameraId });
        console.log("viewCameraStream response:", res);
        const data = res.data;
        const streamingURL =
          Array.isArray(data.data) && data.data[0]
            ? data.data[0].streamingURL
            : null;
        console.info("Fetched streamingURL for camera:", camera.cameraId, streamingURL);
        if (streamingURL) {
          const cleanUrl = streamingURL.replace(/\\u0026/g, "&");
          this.$set(this.draggedData[index], "streamingURL", cleanUrl);
          this.$set(this.draggedData[index], "timeStart", this.getFormattedDateTime());
          // backend may also push websocket update; keeping WebSocket active ensures subsequent updates arrive
          this.startCameraRefresh(camera.cameraId);
        } else {
          console.warn("No streamingURL in response", data);
          this.$vs.notify({
            title: "Failed",
            text: "No streaming URL returned",
            color: "danger",
            position: "top-right",
          });
        }
      } catch (err) {
        console.error("Failed to fetch stream (retry):", err);
        this.$vs.notify({
          title: "Failed",
          text: "Error while loading camera stream",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$set(this.draggedData[index], "loadingStream", false);
      }
    },

    // improve logging inside onCameraPlaced
    onCameraPlaced(evt) {
      try {
        const newIndex = evt.newIndex;
        const addedCamera = this.draggedData[newIndex];
        console.log("Camera placed:", { newIndex, addedCamera });

        if (addedCamera && addedCamera.cameraId) {
          // Initialize WebSocket if not already connected
          if (!this.ws) {
            try {
              const userInfo = JSON.parse(window.localStorage.getItem("userInfo") || "{}");
              const userId = userInfo.id || userInfo.userId;
              if (userId) {
                this.initWebSocket(userId);
              } else {
                console.warn("No userId found in localStorage.userInfo");
              }
            } catch (e) {
              console.warn("Failed to read userInfo from localStorage:", e);
            }
          }

          // Get initial streaming URL
          this.$set(this.draggedData[newIndex], "loadingStream", true);
          api.viewCameraStream({ cameraId: addedCamera.cameraId })
            .then((res) => {
              const data = res.data;
              const streamingURL =
                Array.isArray(data.data) && data.data[0]
                  ? data.data[0].streamingURL
                  : null;

              if (streamingURL) {
                const cleanUrl = streamingURL.replace(/\\u0026/g, "&");
                console.info("Initial streamingURL for placed camera:", addedCamera.cameraId, cleanUrl);
                this.$set(this.draggedData[newIndex], "streamingURL", cleanUrl);
                this.$set(this.draggedData[newIndex], "timeStart", this.getFormattedDateTime());
                
                // Start 5-minute refresh interval AFTER initial URL is fetched
                // This ensures the camera has a valid streamingURL before refresh cycles begin
                this.startCameraRefresh(addedCamera.cameraId);
              } else {
                console.warn("No streamingURL in response for placed camera", data);
              }
            })
            .catch((err) => {
              console.error("Failed to load camera stream:", err);
              this.$vs.notify({
                title: "Failed",
                text: "Error while loading camera stream",
                color: "danger",
                position: "top-right",
              });
            })
            .finally(() => {
              this.$set(this.draggedData[newIndex], "loadingStream", false);
            });
        }
      } catch (e) {
        console.error("Error in onCameraPlaced:", e);
      }
    },

    // =====================================================
    // WEBSOCKET METHODS (Commented out for future use)
    // Uncomment the initWebSocket method above when ready
    // =====================================================
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/player/views.scss";
</style>
